import {AnimatePresence, m} from 'framer-motion';
import {Fragment, useState} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import {Link} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {useBillingUser} from '@common/billing/billing-page/use-billing-user';
import {toast} from '@common/ui/toast/toast';
import {useTrans} from '@common/i18n/use-trans';
import {message} from '@common/i18n/message';
import {Price} from '@common/billing/price';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  landingPage: boolean;
}

export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  landingPage,
}: PricingTableProps) {
  const query = useProducts();
  const {isLoading: subscriptionLoading} = useBillingUser();
  const isLoading = query.isLoading || subscriptionLoading;

  return (
    <div className={className}>
      <AnimatePresence initial={false} mode="wait">
        {!isLoading && query.data ? (
          <PricingLayout
            key="plan-list"
            plans={query.data.products}
            selectedCycle={selectedCycle}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Canonical features ────────────────────────────────────────────────────

const canonicalFeatures = [
  {pattern: /sites$/i, label: 'Sites'},
  {pattern: /AI Generations/i, label: 'AI Generations'},
  {pattern: /storage$/i, label: 'Storage'},
  {pattern: /Drag & Drop/i, label: 'Drag & Drop Editor'},
  {pattern: /AI Site Generation/i, label: 'AI Site Generation'},
  {pattern: /AI Image Generation/i, label: 'AI Image Generation'},
  {pattern: /BYOK/i, label: 'BYOK (Bring Your Own Key)'},
  {pattern: /Templates/i, label: 'Templates'},
  {pattern: /Add Ons/i, label: 'Add Ons: Rhumba AI'},
  {pattern: /History/i, label: 'History + Snapshots'},
  {pattern: /Free.*Subdomain/i, label: 'Free Landed Subdomain'},
  {pattern: /Custom Domain/i, label: 'Custom Domain'},
  {pattern: /Publish/i, label: 'Publish your site'},
  {pattern: /No Landed badge/i, label: 'No Landed badge on site'},
  {pattern: /Download Sites/i, label: 'Download Sites + Export via FTP'},
  {pattern: /Standard Support/i, label: 'Standard Support'},
  {pattern: /Commercial Rights/i, label: 'Commercial Rights'},
  {pattern: /Basic Analytics/i, label: 'Basic Analytics'},
  {pattern: /Webhook/i, label: 'Webhook Integrations (Advanced Lead Capture)'},
  {pattern: /Priority Support/i, label: 'Priority Support'},
  {pattern: /Custom Code|CSS\/JS\/HTML/i, label: 'Custom Code: CSS/JS/HTML Injection'},
  {pattern: /Model Context Protocol/i, label: 'Model Context Protocol'},
  {pattern: /White-labeling/i, label: 'White-labeling on Dashboard & Website'},
  {pattern: /Collaboration/i, label: 'Collaboration (Teams/Workspaces)'},
  {pattern: /VIP Support/i, label: 'VIP Support'},
];

function buildFeatureRows(planFeatures: string[]) {
  const included: {label: string}[] = [];
  const excluded: {label: string}[] = [];

  for (const canon of canonicalFeatures) {
    const match = planFeatures.find(f => canon.pattern.test(f));
    if (match) {
      included.push({label: match});
    } else {
      excluded.push({label: canon.label});
    }
  }

  return {included, excluded};
}

// ── Icons ─────────────────────────────────────────────────────────────────

function CheckSvg() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 6l3 3 5-5" />
    </svg>
  );
}

function XSvg() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3l6 6M9 3l-6 6" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{width: 16, height: 16}}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg className="pricing-faq-arrow" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 7l5 5 5-5" />
    </svg>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────

interface PricingLayoutProps {
  plans: Product[];
  selectedCycle: UpsellBillingCycle;
}

function PricingLayout({plans, selectedCycle}: PricingLayoutProps) {
  const {isLoggedIn} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);
  const freePlan = filteredPlans.find(plan => plan.free);
  const paidPlans = filteredPlans.filter(plan => !plan.free);

  return (
    <m.div {...opacityAnimation}>
      <div className="pricing-page-section">
        {freePlan && <FreeTierBanner plan={freePlan} selectedCycle={selectedCycle} isLoggedIn={isLoggedIn} />}
        <PaidPlanCards plans={paidPlans} selectedCycle={selectedCycle} />
        <CustomPlanCta />
      </div>

      {/* Payment Logos */}
      <div className="pricing-payment-section">
        <p className="pricing-payment-label">Secured payment with PayPal</p>
        <div className="pricing-payment-logos">
          <img src="/assets/images/frontend-pages/visa.png" alt="Visa" />
          <img src="/assets/images/frontend-pages/mastercard.png" alt="Mastercard" />
          <img src="/assets/images/frontend-pages/americanexpress.png" alt="American Express" />
          <img src="/assets/images/frontend-pages/paypal.png" alt="PayPal" />
          <img src="/assets/images/frontend-pages/maestro.png" alt="Maestro" />
          <img src="/assets/images/frontend-pages/jcb.png" alt="JCB" />
          <img src="/assets/images/frontend-pages/dinersclub.png" alt="Diners Club" />
          <img src="/assets/images/frontend-pages/discover.png" alt="Discover" />
        </div>
      </div>

      {/* FAQ Section */}
      <FaqSection />
    </m.div>
  );
}

// ── Free Banner ───────────────────────────────────────────────────────────

function FreeTierBanner({plan, selectedCycle, isLoggedIn}: {plan: Product; selectedCycle: UpsellBillingCycle; isLoggedIn: boolean}) {
  const {user} = useAuth();
  const {subscription} = useBillingUser();
  const {trans} = useTrans();
  const [loading, setLoading] = useState(false);

  const isCurrentPlan = Boolean(
    subscription?.product?.name &&
    subscription.product.name.toLowerCase().includes('free')
  );

  const handleActivate = async () => {
    if (!user) return;
    setLoading(true);

    const host = window.location.origin;
    const successUrl = `${host}/billing/success?product=free&interval=month&count=1`;

    try {
      const res = await apiClient.post('billing/checkout', {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product_id: plan.id,
        success_url: successUrl,
        force_checkout: true,
        interval: 'month',
        interval_count: 1,
      });

      const data = res.data as any;
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data?.url) {
        window.location.href = data.url;
      } else {
        window.location.href = `${host}/billing/success?product=free&interval=month&count=1&immediate=1`;
      }
    } catch (e) {
      console.error('Free plan activation error:', e);
      toast(trans(message('Failed to activate free plan. Please try again.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-free-banner">
      <div className="pricing-free-banner-top">
        <div className="pricing-free-banner-left">
          <span className="pricing-free-badge">Free</span>
          <span className="pricing-free-text">
            Get started with 1 site, 100 AI generations, and 10 GB storage — no credit card required.
          </span>
        </div>
        {!isLoggedIn ? (
          <Link to="/register?redirectFrom=pricing" className="pricing-free-btn">
            Start Free <ArrowIcon />
          </Link>
        ) : isCurrentPlan ? (
          <span className="pricing-free-active">Active Plan</span>
        ) : (
          <button
            onClick={handleActivate}
            disabled={loading}
            className="pricing-free-btn"
          >
            {loading ? 'Activating...' : 'Activate Free Plan'} {!loading && <ArrowIcon />}
          </button>
        )}
      </div>
      <div className="pricing-free-features">
        {plan.feature_list.slice(0, 6).map((feature, i) => (
          <span key={i} className="pricing-free-feature">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {feature}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Paid Cards ────────────────────────────────────────────────────────────

function PaidPlanCards({plans, selectedCycle}: {plans: Product[]; selectedCycle: UpsellBillingCycle}) {
  const {isLoggedIn, user} = useAuth();
  const {subscription} = useBillingUser();
  const {trans} = useTrans();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const currentPlanId = subscription?.product?.name
    ? plans.find(
        plan =>
          plan.name === subscription.product.name &&
          findBestPrice(selectedCycle, plan.prices)?.interval === subscription.price?.interval &&
          Number(findBestPrice(selectedCycle, plan.prices)?.interval_count ?? 1) === Number(subscription.price?.interval_count ?? 1)
      )?.id
    : null;

  const handleCheckout = async (plan: Product, price: Price) => {
    if (!user || !price) return;
    if (currentPlanId === plan.id) return;

    const isScheduled = subscription?.product?.scheduled_changes?.some(
      (change: {product_id: string}) => change.product_id === plan.id
    );
    if (isScheduled) {
      toast(trans(message('You already have a scheduled change to this plan')));
      return;
    }

    setLoadingPlanId(plan.id);

    const hasExistingSubscription = subscription?.product?.name;
    const host = window.location.origin;
    const query = new URLSearchParams({
      product: plan.name.toLowerCase(),
      interval: String(price.interval),
      count: String(price.interval_count ?? 1),
    }).toString();
    const successUrl = `${host}/billing/success?${query}`;

    let payload;
    let endpoint;

    if (hasExistingSubscription) {
      payload = {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product: plan.id,
        interval: String(price.interval),
        interval_count: Number(price.interval_count ?? 1),
      };
      endpoint = 'billing/change-plan';
    } else {
      payload = {
        customer_id: String(user.id),
        email: user.email,
        name: user.display_name || user.email,
        product_id: price.product_id || plan.id,
        success_url: successUrl,
        force_checkout: true,
        interval: String(price.interval),
        interval_count: Number(price.interval_count ?? 1),
      };
      endpoint = 'billing/checkout';
    }

    try {
      const res = await apiClient.post(endpoint, payload);
      const redirect = (res.data as any)?.redirect_url;
      const checkoutUrl = (res.data as any)?.checkout_url;
      const autumnUrl = (res.data as any)?.url;

      if (redirect) {
        window.location.href = redirect;
      } else if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else if (autumnUrl) {
        window.location.href = autumnUrl;
      } else {
        const immediateSuccessUrl = `${window.location.origin}/billing/success?product=${plan.name.toLowerCase()}&interval=${price.interval}&count=${price.interval_count ?? 1}&immediate=1`;
        window.location.href = immediateSuccessUrl;
      }
    } catch (e) {
      console.error('Billing checkout error:', e);
      toast(trans(message('Failed to process billing request. Please try again.')));
    } finally {
      setLoadingPlanId(null);
    }
  };

  return (
    <div className="pricing-page-grid">
      {plans.map((plan, index) => {
        const price = findBestPrice(selectedCycle, plan.prices);
        const isFeatured = plan.recommended || plan.name.toLowerCase().includes('pro');
        const isCurrent = currentPlanId === plan.id;
        const isLoading = loadingPlanId === plan.id;
        const {included, excluded} = buildFeatureRows(plan.feature_list);

        const amount = price
          ? price.interval === 'year'
            ? Math.round(price.amount / 12)
            : Math.round(price.amount / (price.interval_count ?? 1))
          : 0;

        const otherCyclePrice =
          selectedCycle === 'monthly'
            ? findBestPrice('yearly', plan.prices)
            : findBestPrice('monthly', plan.prices);
        const yearlyEquivalentMonthly = otherCyclePrice?.interval === 'year'
          ? Math.round(otherCyclePrice.amount / 12)
          : otherCyclePrice?.amount ?? 0;
        const descText =
          selectedCycle === 'monthly' && otherCyclePrice
            ? `$${yearlyEquivalentMonthly}/mo billed annually`
            : selectedCycle === 'yearly'
              ? `$${amount}/mo billed annually`
              : plan.description;

        const cardClass = [
          'pricing-page-card',
          isFeatured ? 'popular' : '',
          isCurrent ? 'current-plan' : '',
        ].filter(Boolean).join(' ');

        // Determine button style: popular gets pink gradient, last card gets dark, rest get outline
        const isLastCard = index === plans.length - 1;
        const btnClass = isFeatured
          ? 'pricing-btn-plan pricing-btn-primary'
          : isLastCard
            ? 'pricing-btn-plan pricing-btn-dark'
            : 'pricing-btn-plan pricing-btn-outline';

        return (
          <div key={plan.id} className={cardClass}>
            {isFeatured && (
              <div className="pricing-popular-badge">&#9733; Most Popular</div>
            )}

            <div className="pricing-plan-name">{plan.name}</div>

            <div className="pricing-plan-price-row">
              <span className="pricing-plan-currency">$</span>
              <span className="pricing-plan-amount">{amount}</span>
              <span className="pricing-plan-period">/mo</span>
            </div>

            <div className="pricing-plan-note">{descText}</div>
            <div className="pricing-plan-divider" />

            <ul className="pricing-plan-features">
              {included.map((feat, i) => (
                <li key={`inc-${i}`} className="pricing-plan-feature">
                  <span className="feature-check yes"><CheckSvg /></span>
                  {feat.label}
                </li>
              ))}
              {excluded.map((feat, i) => (
                <li key={`exc-${i}`} className="pricing-plan-feature disabled">
                  <span className="feature-check no"><XSvg /></span>
                  {feat.label}
                </li>
              ))}
            </ul>

            {isCurrent ? (
              <button className={btnClass} disabled>
                Current Plan
              </button>
            ) : isLoading ? (
              <button className={btnClass} disabled>
                Redirecting...
              </button>
            ) : !isLoggedIn ? (
              <Link to="/register?redirectFrom=pricing" className={btnClass}>
                Get Started
              </Link>
            ) : (
              <button
                className={btnClass}
                onClick={() => price && handleCheckout(plan, price)}
              >
                Get Started
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Custom CTA ────────────────────────────────────────────────────────────

function CustomPlanCta() {
  return (
    <div className="pricing-custom-cta">
      <div className="pricing-custom-cta-left">
        <h3 className="pricing-custom-cta-title">Need more?</h3>
        <p className="pricing-custom-cta-text">
          Get a custom plan with tailored site limits, AI generations, storage, white-labeling, and dedicated support.
        </p>
      </div>
      <a href="/contact" className="pricing-custom-cta-btn">
        Contact Us <ArrowIcon />
      </a>
    </div>
  );
}

// ── FAQ Section ──────────────────────────────────────────────────────────

const faqItems = [
  {
    question: 'Can I switch plans at any time?',
    answer: 'Yes \u2014 you can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated automatically.',
  },
  {
    question: 'What happens when I hit my site limit?',
    answer: "You'll be prompted to upgrade to a higher tier. Your existing sites remain live and unaffected \u2014 you just won't be able to create new ones until you upgrade.",
  },
  {
    question: 'Do you offer a free trial?',
    answer: 'Landed offers a free tier so you can explore the platform before committing. Paid plans unlock additional storage, sites, and priority support.',
  },
  {
    question: "What's included in Priority Support?",
    answer: 'Priority Support gives you faster response times and dedicated assistance. Available on Pro and Agency plans \u2014 Essentials users receive standard email support.',
  },
  {
    question: 'Can I use my own domain?',
    answer: 'Yes \u2014 all plans include custom domain support. You can connect any domain you own, and every site also gets a free landed.site subdomain.',
  },
];

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="pricing-faq-section">
      <h2 className="pricing-faq-title">Frequently asked questions</h2>
      {faqItems.map((item, i) => (
        <div key={i} className={`pricing-faq-item ${openIndex === i ? 'open' : ''}`}>
          <button
            type="button"
            className="pricing-faq-question"
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
          >
            {item.question}
            <ChevronDown />
          </button>
          <div className="pricing-faq-answer">
            <p>{item.answer}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────

function SkeletonLoader() {
  return (
    <m.div {...opacityAnimation}>
      <div className="pricing-page-section">
        <div className="pricing-free-banner" style={{opacity: 0.5}}>
          <div style={{background: '#f0e0f0', borderRadius: 8, height: 40, width: '100%'}} />
        </div>
        <div className="pricing-page-grid">
          {[0, 1, 2].map(i => (
            <div key={i} className="pricing-page-card" style={{minHeight: 420}}>
              <div style={{background: '#f0e0f0', borderRadius: 8, height: 14, width: 60, marginBottom: 12}} />
              <div style={{background: '#f0e0f0', borderRadius: 8, height: 48, width: 120, marginBottom: 8}} />
              <div style={{background: '#f0e0f0', borderRadius: 8, height: 14, width: 140, marginBottom: 28}} />
              {[0, 1, 2, 3].map(j => (
                <div key={j} style={{background: '#f0e0f0', borderRadius: 8, height: 14, width: '80%', marginBottom: 16}} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </m.div>
  );
}
